import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { 
  Users, Gift, Copy, Check, RefreshCw, DollarSign, 
  Award, Share2, Link2, ChevronRight, TrendingUp, Zap,
  MessageSquare, Star
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralProgram = () => {
  const [referralData, setReferralData] = useState(null);
  const [myReferrals, setMyReferrals] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const token = localStorage.getItem('token');

  const fetchReferralData = useCallback(async () => {
    try {
      const [linkRes, referralsRes, leaderboardRes] = await Promise.all([
        fetch(`${API_URL}/api/referrals/my-link`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/referrals/my-referrals`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/referrals/leaderboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (linkRes.ok) {
        const data = await linkRes.json();
        setReferralData(data);
      }

      if (referralsRes.ok) {
        const data = await referralsRes.json();
        setMyReferrals(data.referrals || []);
      }

      if (leaderboardRes.ok) {
        const data = await leaderboardRes.json();
        setLeaderboard(data.leaderboard || []);
      }
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchReferralData();
  }, [fetchReferralData]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferral = () => {
    if (navigator.share && referralData) {
      navigator.share({
        title: 'Join Jiffys Mobile',
        text: referralData.share_message,
        url: referralData.referral_link
      }).catch(() => {
        copyToClipboard(referralData.referral_link);
      });
    } else {
      copyToClipboard(referralData?.referral_link || '');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="referral-program">
      {/* Hero Card */}
      <Card className="bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 text-white border-0 overflow-hidden relative">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10" />
        <CardContent className="p-6 sm:p-8 relative z-10">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Gift className="w-10 h-10 text-yellow-300" />
              </div>
            </div>
            
            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-2xl sm:text-3xl font-bold mb-2">
                Earn $1,499.95 Per Referral!
              </h2>
              <p className="text-purple-100 mb-4">
                Share your unique link and earn when your referrals complete 750 deliveries.
                Plus, they get $899.99 just for joining!
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center sm:justify-start">
                <div className="bg-white/20 rounded-lg px-4 py-2 backdrop-blur-sm">
                  <p className="text-xs text-purple-200">New Driver Gets</p>
                  <p className="text-xl font-bold text-yellow-300">$899.99</p>
                </div>
                <div className="bg-white/20 rounded-lg px-4 py-2 backdrop-blur-sm">
                  <p className="text-xs text-purple-200">You Earn</p>
                  <p className="text-xl font-bold text-green-300">$1,499.95</p>
                </div>
                <div className="bg-white/20 rounded-lg px-4 py-2 backdrop-blur-sm">
                  <p className="text-xs text-purple-200">After</p>
                  <p className="text-xl font-bold text-blue-300">750 Deliveries</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referral Link Card */}
      {referralData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-purple-600" />
              Your Unique Referral Link
            </CardTitle>
            <CardDescription>
              Share this link to invite new drivers. Code: <Badge variant="outline" className="ml-1">{referralData.referral_code}</Badge>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                readOnly
                value={referralData.referral_link}
                className="font-mono text-sm bg-gray-50"
                data-testid="referral-link-input"
              />
              <Button
                variant="outline"
                onClick={() => copyToClipboard(referralData.referral_link)}
                data-testid="copy-link-btn"
              >
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button onClick={shareReferral} className="bg-purple-600 hover:bg-purple-700">
                <Share2 className="h-4 w-4 mr-2" />
                Share Link
              </Button>
              <Button variant="outline" onClick={() => copyToClipboard(referralData.share_message)}>
                <MessageSquare className="h-4 w-4 mr-2" />
                Copy Message
              </Button>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t">
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{referralData.total_referrals || 0}</p>
                <p className="text-xs text-gray-500">Total Referrals</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{referralData.successful_referrals || 0}</p>
                <p className="text-xs text-gray-500">Completed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">${(referralData.total_earned || 0).toFixed(2)}</p>
                <p className="text-xs text-gray-500">Total Earned</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Referrals */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            My Referrals
          </CardTitle>
          <CardDescription>Track your referral progress</CardDescription>
        </CardHeader>
        <CardContent>
          {myReferrals.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="font-medium">No referrals yet</p>
              <p className="text-sm">Share your link to start earning!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {myReferrals.map((referral, index) => (
                <div 
                  key={referral.application_id || index}
                  className="p-4 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Users className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">New Driver</p>
                        <p className="text-sm text-gray-500">
                          Joined {new Date(referral.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <Badge className={
                        referral.referrer_bonus_status === 'paid' 
                          ? 'bg-green-100 text-green-800'
                          : referral.referrer_bonus_status === 'ready_to_pay'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-blue-100 text-blue-800'
                      }>
                        {referral.referrer_bonus_status === 'paid' ? 'Bonus Paid!' 
                          : referral.referrer_bonus_status === 'ready_to_pay' ? 'Ready for Payout'
                          : `${referral.deliveries_completed || 0}/750 deliveries`}
                      </Badge>
                      
                      {referral.referrer_bonus_status === 'tracking' && (
                        <div className="mt-2">
                          <div className="w-32 h-2 bg-gray-200 rounded-full">
                            <div 
                              className="h-full bg-purple-600 rounded-full transition-all"
                              style={{ width: `${((referral.deliveries_completed || 0) / 750) * 100}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-yellow-500" />
              Top Referrers
            </CardTitle>
            <CardDescription>See who's earning the most!</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {leaderboard.map((user, index) => (
                <div 
                  key={user.user_id || index}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    index === 0 ? 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200 border' :
                    index === 1 ? 'bg-gradient-to-r from-gray-50 to-gray-100' :
                    index === 2 ? 'bg-gradient-to-r from-orange-50 to-orange-100' :
                    'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-300 text-gray-700' :
                      index === 2 ? 'bg-orange-300 text-orange-800' :
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {index + 1}
                    </div>
                    <span className="font-medium">{user.user_name}</span>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-green-600">${(user.total_earned || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-500">{user.successful_referrals} referrals</p>
                    </div>
                    {index < 3 && (
                      <Star className={`h-5 w-5 ${
                        index === 0 ? 'text-yellow-500 fill-yellow-500' :
                        index === 1 ? 'text-gray-400 fill-gray-400' :
                        'text-orange-400 fill-orange-400'
                      }`} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReferralProgram;
